import React from 'react';
import { data } from 'hudl-base';
import { getConsentState } from '../../utility/consent-utils';
import applyTargeting from '../../utility/ads/apply-targeting';
import applyFreestarPrivacy from '../../utility/ads/apply-freestar-privacy';
import './freestar-ad-slot.scss';

// A regular (non-sticky) Freestar ad. Asks Freestar for the ad once we know the
// viewer's cookie choice, and removes it on unmount. Same mount/unmount pattern
// as Freestar's own React component:
// https://freestarhelp.zendesk.com/hc/en-us/articles/34295925113876-React-Tagging
// Freestar's CSS reserves the ad's height, so we only hide the box when no ad comes back.
class FreestarAdSlot extends React.Component {
    static propTypes = {
        placementName: React.PropTypes.string.isRequired,
    };

    componentDidMount() {
        if (typeof window === 'undefined' || !window.freestar) return;

        this._mounted = true;
        const { placementName } = this.props;

        window.freestar.queue = window.freestar.queue || [];
        window.freestar.queue.push(() => {
            if (!this._mounted) return;

            const adData = data.get('ads');
            const targetingData = adData?.targetingData;
            const backendRequiresNpa = targetingData?.nonPersonalizedAds === 'True';

            getConsentState().then(consentState => {
                if (!this._mounted) return;

                // pubads() doesn't exist until gpt.js loads, and the freestar queue can drain first.
                window.googletag = window.googletag || {};
                window.googletag.cmd = window.googletag.cmd || [];
                window.googletag.cmd.push(() => {
                    if (!this._mounted || typeof window.freestar.newAdSlots !== 'function') return;

                    applyFreestarPrivacy(consentState.hasTargeting, backendRequiresNpa);

                    // Freestar creates its own Google ad slot, so our targeting keys go on the
                    // page and are also passed in with the request.
                    const targeting = {};
                    if (targetingData) {
                        applyTargeting(window.googletag.pubads(), targetingData);
                        applyTargeting({ setTargeting: (k, v) => { targeting[k] = String(v); } }, targetingData);
                    }

                    this.slotRenderHandler = event => {
                        if (!event.slot || event.slot.getSlotElementId() !== placementName) return;
                        if (this._containerEl) this._containerEl.style.display = event.isEmpty ? 'none' : '';
                    };
                    window.googletag.pubads().addEventListener('slotRenderEnded', this.slotRenderHandler);

                    window.freestar.newAdSlots([{ placementName, slotId: placementName, targeting }]);
                    this._registered = true;

                    // If the viewer changes their cookie choice later, update the privacy
                    // settings so the next refresh respects it.
                    this.handleConsentChange = () => {
                        getConsentState().then(({ hasTargeting }) => {
                            window.googletag.cmd.push(() => applyFreestarPrivacy(hasTargeting, backendRequiresNpa));
                        });
                    };
                    window.addEventListener('ConsentScriptsReady', this.handleConsentChange);
                });
            });
        });
    }

    componentWillUnmount() {
        this._mounted = false;
        const { placementName } = this.props;

        if (this.handleConsentChange) {
            window.removeEventListener('ConsentScriptsReady', this.handleConsentChange);
        }

        if (!this._registered) return;

        const googletag = window.googletag;
        if (this.slotRenderHandler && googletag?.pubads && typeof googletag.pubads().removeEventListener === 'function') {
            googletag.pubads().removeEventListener('slotRenderEnded', this.slotRenderHandler);
        }

        window.freestar.queue = window.freestar.queue || [];
        window.freestar.queue.push(() => {
            // Pubfig's deleteAdSlots takes element id strings.
            if (typeof window.freestar.deleteAdSlots === 'function') {
                window.freestar.deleteAdSlots([placementName]);
            }
        });

        // Destroy the GPT slot too, so Freestar's refresh can't request it after a
        // resize swaps to the footer. Same fallback the fan site uses.
        if (googletag?.cmd) {
            googletag.cmd.push(() => {
                const slot = googletag.pubads().getSlots().find(s => s.getSlotElementId() === placementName);
                if (slot) googletag.destroySlots([slot]);
            });
        }
    }

    render() {
        return (
            <div className="freestar-ad-slot" ref={el => { this._containerEl = el; }}>
                <div id={this.props.placementName} />
            </div>
        );
    }
}

export default FreestarAdSlot;
